/**
 * EduBattle's sounds for the quick games – all synthesised with the Web Audio API, no files to download.
 *
 *  - The audio context is only created after the first tap / key press (browsers demand that).
 *  - One switch for everything (the speaker button in the arena); the choice is remembered. In a class the sound starts off.
 *  - Every cue is short, soft and friendly: a wrong answer is a gentle "boop", never a buzzer – we only reward, we never scold.
 *  - Phones get a tiny vibration on top (where the browser allows it), but only while the sound is on.
 *
 * EduPoly and Tårnforsvar have their own sound engines; this module is for everything else.
 */
export type Cue =
  | "click" | "correct" | "wrong" | "place" | "place2" | "drop" | "move" | "capture" | "check"
  | "turn" | "tick" | "tickHi" | "count" | "go" | "coin" | "streak" | "levelup" | "win" | "lose" | "draw"
  | "deal" | "flip" | "play" | "edu" | "cannon" | "hit" | "miss" | "sink" | "step" | "key" | "gate" | "treasure" | "bump" | "boost" | "pass" | "lock"
  | "dice" | "unlock" | "pair" | "slip" | "home" | "found"
  /* v22: the sounds of the website's own buttons (see installButtonSounds) */
  | "tap" | "tapBig" | "back" | "nav" | "on" | "off" | "pick" | "denied";

const KEY = "edu-snd";

type AudioGlobals = { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let noiseBuf: AudioBuffer | null = null;
let enabled: boolean | null = null; // null = not decided yet (decided by the arena the first time: off in class)
let unlocked = false;
const listeners = new Set<() => void>();
const lastPlayed = new Map<Cue, number>();

function readPref(): boolean | null {
  try {
    const v = localStorage.getItem(KEY);
    if (v === "1") return true;
    if (v === "0") return false;
  } catch { /* storage may be blocked */ }
  return null;
}

/** The arena calls this once per match: the saved choice wins, otherwise sound is on – except in a class, where it starts off. */
export function initSound(inClass: boolean) {
  const saved = readPref();
  enabled = saved ?? !inClass;
  listeners.forEach((l) => l());
}
export function soundOn(): boolean { return enabled ?? readPref() ?? true; }
export function setSound(on: boolean) {
  enabled = on;
  try { localStorage.setItem(KEY, on ? "1" : "0"); } catch { /* ignore */ }
  if (on) { unlock(); play("click"); }
  listeners.forEach((l) => l());
}
export function subscribeSound(fn: () => void) { listeners.add(fn); return () => { listeners.delete(fn); }; }

/** Create / resume the audio context. Must be called from a user gesture; `installSoundUnlock` does it for the first tap or key press. */
export function unlock() {
  if (typeof window === "undefined") return;
  if (ctx) { if (ctx.state === "suspended") void ctx.resume().catch(() => undefined); return; }
  try {
    const g = globalThis as unknown as AudioGlobals;
    const C = g.AudioContext ?? g.webkitAudioContext;
    if (!C) return;
    const c = new C();
    const m = c.createGain(); m.gain.value = 0.5;
    const comp = c.createDynamicsCompressor(); comp.threshold.value = -16; comp.ratio.value = 4;
    m.connect(comp); comp.connect(c.destination);
    const len = Math.floor(c.sampleRate * 0.8), buf = c.createBuffer(1, len, c.sampleRate), d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    ctx = c; master = m; noiseBuf = buf; unlocked = true;
  } catch { ctx = null; }
}
export function installSoundUnlock(): () => void {
  if (typeof document === "undefined") return () => {};
  const h = () => unlock();
  document.addEventListener("pointerdown", h, { passive: true });
  document.addEventListener("keydown", h, { passive: true });
  const off = installButtonSounds();
  return () => { document.removeEventListener("pointerdown", h); document.removeEventListener("keydown", h); off(); };
}

/* ───── building blocks ───── */
interface ToneOpts { type?: OscillatorType; gain?: number; to?: number; attack?: number; detune?: number }
function tone(freq: number, t0: number, dur: number, o: ToneOpts = {}) {
  if (!ctx || !master) return;
  const g = ctx.createGain(), osc = ctx.createOscillator();
  osc.type = o.type ?? "sine";
  osc.frequency.setValueAtTime(freq, t0);
  if (o.to) osc.frequency.exponentialRampToValueAtTime(Math.max(20, o.to), t0 + dur);
  if (o.detune) osc.detune.value = o.detune;
  const peak = o.gain ?? 0.25, att = o.attack ?? 0.006;
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(peak, t0 + att);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g); g.connect(master);
  osc.start(t0); osc.stop(t0 + dur + 0.03);
}
interface NoiseOpts { type?: BiquadFilterType; f0?: number; f1?: number; q?: number; gain?: number; attack?: number }
function noise(t0: number, dur: number, o: NoiseOpts = {}) {
  if (!ctx || !master || !noiseBuf) return;
  const src = ctx.createBufferSource(); src.buffer = noiseBuf;
  const f = ctx.createBiquadFilter(); f.type = o.type ?? "lowpass"; f.Q.value = o.q ?? 0.8;
  f.frequency.setValueAtTime(o.f0 ?? 1200, t0);
  if (o.f1) f.frequency.exponentialRampToValueAtTime(Math.max(30, o.f1), t0 + dur);
  const g = ctx.createGain(), peak = o.gain ?? 0.3, att = o.attack ?? 0.004;
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(peak, t0 + att);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  src.connect(f); f.connect(g); g.connect(master);
  src.start(t0, Math.random() * 0.2); src.stop(t0 + dur + 0.03);
}
const NOTE = (n: number) => 440 * Math.pow(2, (n - 69) / 12); // MIDI number → Hz
const arp = (t: number, notes: number[], step: number, o: ToneOpts = {}) => notes.forEach((n, i) => tone(NOTE(n), t + i * step, 0.28, { type: "triangle", gain: 0.2, ...o }));

const CUES: Record<Cue, (t: number) => void> = {
  click: (t) => tone(880, t, 0.06, { type: "triangle", gain: 0.12 }),
  correct: (t) => { arp(t, [72, 76, 79], 0.075, { gain: 0.22 }); tone(NOTE(84), t + 0.22, 0.4, { type: "sine", gain: 0.16 }); },
  wrong: (t) => { tone(330, t, 0.28, { type: "sine", gain: 0.2, to: 220 }); tone(262, t + 0.09, 0.3, { type: "sine", gain: 0.12, to: 196 }); },
  place: (t) => { tone(300, t, 0.09, { type: "sine", gain: 0.3, to: 140 }); noise(t, 0.05, { f0: 2400, gain: 0.12 }); },
  place2: (t) => { tone(420, t, 0.09, { type: "sine", gain: 0.28, to: 200 }); noise(t, 0.05, { f0: 3000, gain: 0.1 }); },
  drop: (t) => { tone(900, t, 0.18, { type: "sine", gain: 0.1, to: 500 }); tone(160, t + 0.32, 0.14, { type: "sine", gain: 0.32, to: 70 }); noise(t + 0.32, 0.07, { f0: 1500, gain: 0.14 }); },
  move: (t) => { tone(260, t, 0.07, { type: "sine", gain: 0.26, to: 150 }); noise(t, 0.04, { f0: 2200, gain: 0.09 }); },
  capture: (t) => { tone(220, t, 0.1, { type: "triangle", gain: 0.3, to: 110 }); noise(t, 0.09, { f0: 1800, gain: 0.2 }); tone(190, t + 0.09, 0.1, { type: "sine", gain: 0.2, to: 90 }); },
  check: (t) => { tone(NOTE(81), t, 0.14, { type: "square", gain: 0.07 }); tone(NOTE(86), t + 0.13, 0.22, { type: "square", gain: 0.07 }); },
  turn: (t) => { tone(NOTE(79), t, 0.16, { type: "sine", gain: 0.16 }); tone(NOTE(84), t + 0.1, 0.22, { type: "sine", gain: 0.14 }); },
  tick: (t) => tone(1100, t, 0.04, { type: "square", gain: 0.05 }),
  tickHi: (t) => { tone(1500, t, 0.07, { type: "square", gain: 0.08 }); },
  count: (t) => tone(NOTE(69), t, 0.18, { type: "triangle", gain: 0.2 }),
  go: (t) => { tone(NOTE(81), t, 0.34, { type: "triangle", gain: 0.24 }); tone(NOTE(88), t + 0.02, 0.34, { type: "sine", gain: 0.1 }); },
  coin: (t) => { tone(NOTE(83), t, 0.09, { type: "square", gain: 0.07 }); tone(NOTE(88), t + 0.08, 0.32, { type: "square", gain: 0.07 }); },
  streak: (t) => arp(t, [76, 79, 83, 88], 0.06, { gain: 0.16, type: "sine" }),
  levelup: (t) => { arp(t, [67, 72, 76, 79, 84], 0.09, { gain: 0.22 }); tone(NOTE(91), t + 0.5, 0.6, { type: "sine", gain: 0.14 }); },
  win: (t) => { arp(t, [67, 72, 76, 79], 0.11, { gain: 0.24 }); [72, 76, 79, 84].forEach((n) => tone(NOTE(n), t + 0.5, 0.9, { type: "triangle", gain: 0.12 })); tone(NOTE(96), t + 0.55, 0.7, { type: "sine", gain: 0.08 }); },
  lose: (t) => { arp(t, [69, 67, 64], 0.16, { gain: 0.15, type: "sine" }); },
  draw: (t) => { tone(NOTE(67), t, 0.2, { type: "triangle", gain: 0.16 }); tone(NOTE(67), t + 0.2, 0.3, { type: "triangle", gain: 0.14 }); },
  deal: (t) => noise(t, 0.09, { type: "bandpass", f0: 2600, f1: 1200, q: 0.6, gain: 0.16 }),
  flip: (t) => { noise(t, 0.07, { type: "bandpass", f0: 3200, f1: 1500, q: 0.7, gain: 0.14 }); tone(200, t + 0.03, 0.05, { type: "sine", gain: 0.12, to: 120 }); },
  play: (t) => { noise(t, 0.08, { type: "bandpass", f0: 2800, f1: 1400, q: 0.7, gain: 0.16 }); tone(180, t + 0.05, 0.08, { type: "sine", gain: 0.26, to: 100 }); },
  edu: (t) => { arp(t, [76, 83, 88], 0.07, { gain: 0.2, type: "square" }); },
  cannon: (t) => { tone(140, t, 0.3, { type: "sawtooth", gain: 0.22, to: 45 }); noise(t, 0.25, { f0: 900, f1: 180, gain: 0.3 }); },
  hit: (t) => { noise(t, 0.5, { f0: 1600, f1: 120, gain: 0.5 }); tone(110, t, 0.45, { type: "sawtooth", gain: 0.25, to: 36 }); tone(NOTE(88), t + 0.08, 0.14, { type: "sine", gain: 0.05 }); },
  miss: (t) => { noise(t, 0.35, { type: "bandpass", f0: 3400, f1: 900, q: 0.6, gain: 0.22 }); tone(520, t + 0.03, 0.2, { type: "sine", gain: 0.06, to: 260 }); },
  sink: (t) => { noise(t, 0.9, { f0: 1500, f1: 80, gain: 0.45 }); tone(130, t, 0.9, { type: "sawtooth", gain: 0.2, to: 30 }); [0.35, 0.5, 0.62].forEach((d, i) => tone(500 + i * 130, t + d, 0.12, { type: "sine", gain: 0.08, to: 900 + i * 100 })); },
  step: (t) => { tone(190, t, 0.05, { type: "triangle", gain: 0.1, to: 120 }); noise(t, 0.03, { f0: 1800, gain: 0.05 }); },
  key: (t) => { arp(t, [84, 88, 91, 96], 0.05, { gain: 0.15, type: "sine" }); noise(t + 0.18, 0.1, { type: "highpass", f0: 5000, gain: 0.05 }); },
  gate: (t) => { tone(95, t, 0.5, { type: "sawtooth", gain: 0.09, to: 160 }); noise(t, 0.4, { f0: 500, f1: 900, gain: 0.07 }); tone(120, t + 0.45, 0.2, { type: "sine", gain: 0.3, to: 55 }); },
  treasure: (t) => { arp(t, [72, 76, 79, 84, 88, 91, 96], 0.07, { gain: 0.2 }); [84, 88, 91].forEach((n) => tone(NOTE(n), t + 0.55, 1, { type: "triangle", gain: 0.1 })); noise(t + 0.4, 0.5, { type: "highpass", f0: 6000, gain: 0.06 }); },
  bump: (t) => { tone(130, t, 0.1, { type: "triangle", gain: 0.2, to: 90 }); },
  boost: (t) => { noise(t, 0.3, { type: "bandpass", f0: 700, f1: 3200, q: 1, gain: 0.14 }); tone(300, t, 0.3, { type: "sine", gain: 0.1, to: 900 }); },
  pass: (t) => { noise(t, 0.28, { type: "bandpass", f0: 600, f1: 2600, q: 1.2, gain: 0.16 }); tone(NOTE(79), t + 0.18, 0.2, { type: "sine", gain: 0.12 }); },
  lock: (t) => { tone(200, t, 0.22, { type: "sine", gain: 0.14, to: 140 }); },
  /* v16: a handful of dice clacking on a table, then a tiny wooden tock */
  dice: (t) => { [0, 0.05, 0.1, 0.17, 0.26].forEach((d, i) => { noise(t + d, 0.045, { type: "bandpass", f0: 2200 + i * 260, f1: 1100, q: 1.1, gain: 0.17 - i * 0.02 }); tone(260 + i * 40, t + d, 0.05, { type: "triangle", gain: 0.1, to: 140 }); }); tone(190, t + 0.34, 0.08, { type: "sine", gain: 0.16, to: 120 }); },
  unlock: (t) => { tone(140, t, 0.2, { type: "triangle", gain: 0.12, to: 220 }); arp(t + 0.12, [74, 79, 83, 86], 0.06, { gain: 0.15, type: "sine" }); noise(t + 0.3, 0.25, { type: "highpass", f0: 5500, gain: 0.05 }); },
  pair: (t) => { arp(t, [79, 84], 0.09, { gain: 0.2, type: "sine" }); tone(NOTE(91), t + 0.2, 0.35, { type: "triangle", gain: 0.1 }); },
  slip: (t) => { tone(280, t, 0.18, { type: "sine", gain: 0.1, to: 230 }); },
  home: (t) => { arp(t, [72, 79, 84], 0.08, { gain: 0.2 }); tone(NOTE(91), t + 0.26, 0.4, { type: "sine", gain: 0.12 }); },
  found: (t) => { arp(t, [76, 79, 83, 88], 0.07, { gain: 0.18, type: "sine" }); noise(t + 0.22, 0.3, { type: "highpass", f0: 6000, gain: 0.05 }); },
  /* v22 – every button has its own friendly little sound; the kind of button decides which */
  tap: (t) => { tone(NOTE(79), t, 0.08, { type: "triangle", gain: 0.13, to: NOTE(76) }); noise(t, 0.02, { type: "highpass", f0: 4500, gain: 0.03 }); },
  tapBig: (t) => { tone(NOTE(72), t, 0.1, { type: "triangle", gain: 0.17 }); tone(NOTE(79), t + 0.06, 0.12, { type: "triangle", gain: 0.16 }); tone(NOTE(84), t + 0.12, 0.2, { type: "sine", gain: 0.1 }); },
  back: (t) => { tone(NOTE(76), t, 0.07, { type: "triangle", gain: 0.12 }); tone(NOTE(71), t + 0.05, 0.12, { type: "triangle", gain: 0.11 }); },
  nav: (t) => { tone(NOTE(81), t, 0.05, { type: "sine", gain: 0.13 }); tone(NOTE(88), t + 0.04, 0.1, { type: "sine", gain: 0.1 }); },
  on: (t) => { tone(NOTE(74), t, 0.07, { type: "sine", gain: 0.15 }); tone(NOTE(81), t + 0.06, 0.12, { type: "sine", gain: 0.13 }); },
  off: (t) => { tone(NOTE(81), t, 0.07, { type: "sine", gain: 0.13 }); tone(NOTE(74), t + 0.06, 0.12, { type: "sine", gain: 0.11 }); },
  pick: (t) => { tone(NOTE(86), t, 0.05, { type: "sine", gain: 0.13, to: NOTE(90) }); noise(t, 0.015, { type: "highpass", f0: 6000, gain: 0.03 }); },
  denied: (t) => { tone(NOTE(62), t, 0.1, { type: "sine", gain: 0.13, to: NOTE(60) }); tone(NOTE(58), t + 0.07, 0.12, { type: "sine", gain: 0.1 }); },
};

/** Cues that may fire in quick succession are thinned out so that nothing ever turns into a rattle. */
const MIN_GAP: Partial<Record<Cue, number>> = { tick: 0.12, tickHi: 0.12, click: 0.04, step: 0.07, correct: 0.15, wrong: 0.15, tap: 0.05, tapBig: 0.08, back: 0.05, nav: 0.05, on: 0.05, off: 0.05, pick: 0.04, denied: 0.1 };

/** when (performance.now) any cue was last asked for – the button sounds wait for this, so a button that has a sound of its own never gets two */
let lastAsked = -1e9;
export function play(cue: Cue) {
  lastAsked = typeof performance !== "undefined" ? performance.now() : Date.now();
  if (!soundOn()) return;
  if (!unlocked) unlock();
  if (!ctx || !master) return;
  if (ctx.state === "suspended") { void ctx.resume().catch(() => undefined); }
  const now = ctx.currentTime;
  const gap = MIN_GAP[cue] ?? 0.02;
  if (now - (lastPlayed.get(cue) ?? -9) < gap) return;
  lastPlayed.set(cue, now);
  try { CUES[cue](now + 0.005); } catch { /* a failing sound must never break a game */ }
}

/* ═════════ v22: a sound on EVERY button of the website ═════════
 * One listener for the whole page (so a new button can never be forgotten): every click on something clickable plays a small cue, chosen by the kind of
 * button. A button that plays a sound of its own (an answer, a coin, a level-up) is left alone – the page-wide sound only fills in where nothing else sounds.
 * `data-sfx="<cue>"` on an element picks a cue by hand. The sound switch (speaker in the top bar) silences all of it. */
/** the bits of an element that decide its sound – small on purpose, so it can be tested without a browser */
export interface ControlLike {
  tagName: string; className?: unknown; type?: string; checked?: boolean; href?: string;
  getAttribute(name: string): string | null; hasAttribute(name: string): boolean;
  closest?(sel: string): unknown;
}
const CUE_NAMES = new Set<string>(Object.keys(CUES));
const BACK_WORDS = /^(tilbage|luk|annuller|fortryd|nej|senere|skip|spring over|forlad|stop)\b/i;
export function cueFor(el: ControlLike): Cue {
  const manual = el.getAttribute("data-sfx");
  if (manual && CUE_NAMES.has(manual)) return manual as Cue;
  const tag = el.tagName.toUpperCase(), cls = typeof el.className === "string" ? el.className : "", role = el.getAttribute("role") ?? "";
  if (el.getAttribute("aria-disabled") === "true") return "denied";
  if (tag === "INPUT") {
    const t = (el.type ?? el.getAttribute("type") ?? "").toLowerCase();
    if (t === "checkbox" || t === "radio") return el.checked ? "on" : "off";
    if (t === "range") return "pick";
    return "tap";
  }
  if (tag === "SELECT") return "pick";
  if (role === "switch" || role === "checkbox") return el.getAttribute("aria-checked") === "true" ? "on" : "off";
  if (role === "tab" || role === "menuitem" || role === "option" || role === "radio") return "pick";
  const label = ((el.getAttribute("aria-label") ?? "") + " " + (el.getAttribute("title") ?? "")).trim();
  if (/\b(close|luk|back|tilbage|annuller)\b/i.test(label) || /\b(bd-close|modal-x|close|x-btn|backbtn)\b/.test(cls) || BACK_WORDS.test(label)) return "back";
  if (tag === "A" && el.closest && (el.closest(".nav, .tabbar, .topnav, [role=navigation], nav") ?? null)) return "nav";
  if (/\b(btn)\b/.test(cls) && /\b(ghost|small|link)\b/.test(cls)) return "tap";
  if (/\b(btn|bigplay|cta|play|gold|big|claimbtn|cb-btn)\b/.test(cls) && !/\b(ghost|small)\b/.test(cls)) return "tapBig";
  if (/\b(chip|tile|card|choice|opt|pill|swatch)\b/.test(cls)) return "pick";
  return "tap";
}
const CLICKABLE = "button, a[href], [role=button], [role=tab], [role=menuitem], [role=switch], [role=option], [role=checkbox], [role=radio], summary, input, select, [data-sfx]";
/** the thing the person actually pressed: the closest button, link or other clickable (a div with a pointer cursor counts too) */
export function controlOf(target: unknown): Element | null {
  let el = target instanceof Element ? target : null;
  for (let i = 0; el && i < 7; i++, el = el.parentElement) {
    if (el.matches(CLICKABLE)) { if (el.tagName === "LABEL") return null; return el; }
    if (el.tagName === "LABEL" && (el as HTMLLabelElement).control) return null; // the click on the box inside follows by itself
    try { if (getComputedStyle(el).cursor === "pointer") return el; } catch { /* no styles */ }
  }
  return null;
}
export function installButtonSounds(): () => void {
  if (typeof document === "undefined") return () => {};
  const h = (e: Event) => {
    const el = controlOf(e.target);
    if (!el) return;
    const at = performance.now(), cue = cueFor(el);
    // after the whole click has been handled: did the button play something itself? then this one stays quiet
    setTimeout(() => { if (lastAsked >= at) return; play(cue); if (cue === "tapBig") buzz(6); }, 0);
  };
  document.addEventListener("click", h, true);
  return () => document.removeEventListener("click", h, true);
}

/** A short vibration on phones (only while the sound is on – the same switch covers both). */
export function buzz(ms: number | number[]) {
  try {
    if (!soundOn()) return;
    if (typeof navigator !== "undefined" && "vibrate" in navigator) navigator.vibrate(ms);
  } catch { /* not supported */ }
}
